/** STAREA ȘANTIERULUI într-o săptămână, citită din program.
 *
 *  Nimic de aici nu inventează lucru: fiecare pachet al programului
 *  (corp × nivel × etapă) are de la motor desfășurarea lui pe săptămâni
 *  calendaristice (`desfasurare`) și sub-fazele lui ca fracții din durată
 *  (`faze`: cofraj → armare → beton → decofrare pe structură, săpătură →
 *  fundații pe terasamente). Aici se citește, pentru o săptămână dată, cât
 *  din fiecare pachet e făcut și în ce fază e, iar scena 3D desenează exact
 *  asta. Săptămâna e cea calendaristică, de luni (D6, 2.11.2026). */
import type { EtapaProgram, FazaPachet, Program } from "../cost/program";

export type Fractie = { f: number; faza: FazaPachet["faza"] | null; gata: boolean; inceput: boolean };

/** Lunea săptămânii calendaristice a datei. */
export function lunea(d: Date): Date {
  const l = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  l.setDate(l.getDate() - ((l.getDay() + 6) % 7));
  return l;
}
const ISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const iso = ISO;

/** Săptămânile programului: lunea fiecăreia, de la cea a startului la cea a sfârșitului. */
export function saptamanile(p: Program): Date[] {
  const a = lunea(new Date(p.start + "T00:00:00")), b = lunea(new Date(p.sfarsit + "T00:00:00"));
  const out: Date[] = [];
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 7)) out.push(new Date(d));
  return out;
}

/** Toate pachetele programului, într-o listă. */
export function pachetele(p: Program): EtapaProgram[] {
  const out: EtapaProgram[] = [];
  for (const c of [...p.corpuri, ...(p.subsoluri ?? [])]) {
    for (const n of c.niveluri) out.push(...n.etape);
    out.push(...c.etape);
  }
  out.push(...p.santier);
  return out;
}

/** Fracția pachetului la SFÂRȘITUL săptămânii care începe luni `l`: din
 *  desfășurarea scrisă de motor; 0 înaintea primei săptămâni, capătul
 *  desfășurării (1, sau mai mult cu decofrarea) după ultima. */
export function fractia(e: EtapaProgram, l: Date): number {
  const d = e.desfasurare ?? [];
  const k = ISO(l);
  if (!d.length) {
    // program mai vechi (fără desfășurare): pe datele pachetului, liniar
    const a = new Date(e.start + "T00:00:00").getTime(), b = new Date(e.sfarsit + "T00:00:00").getTime();
    const t = l.getTime() + 6 * 86400000;
    if (t < a) return 0;
    if (t >= b || b <= a) return 1;
    return (t - a) / (b - a);
  }
  if (k < d[0].luni) return 0;
  let f = 0;
  for (const w of d) {
    if (w.luni <= k) f = w.pana_la;
    else break;
  }
  return f;
}

/** Faza în care e pachetul la fracția `f` (`null` neînceput). */
export function faza(e: EtapaProgram, f: number): FazaPachet["faza"] | null {
  if (f <= 0) return null;
  const faze = e.faze ?? [{ faza: "lucru", de_la: 0, pana_la: 1 }];
  for (const x of faze) if (f <= x.pana_la + 1e-9) return x.faza;
  return faze[faze.length - 1]?.faza ?? "lucru";
}

/** Starea pachetului la sfârșitul săptămânii de luni `l`. */
export function stare(e: EtapaProgram, l: Date): Fractie {
  const f = fractia(e, l);
  const cap = Math.max(...(e.faze ?? []).map((x) => x.pana_la), 1);
  return { f, faza: faza(e, f), inceput: f > 0, gata: f >= cap - 1e-9 };
}

/** Cât din fiecare tronson e făcut la fracția `f` a unui pachet de nivel:
 *  tronsoanele se iau pe rând, în ordinea și cu ponderea date de motor
 *  (`front.tronsoane`). Fără front, tot pachetul e un singur tronson. */
export function peTronsoane(e: EtapaProgram, f: number): Record<string, number> {
  const tr = e.front?.tronsoane;
  if (!tr?.length) return { [e.corp]: Math.min(Math.max(f, 0), 1) };
  const out: Record<string, number> = {};
  let cum = 0;
  const g = Math.min(Math.max(f, 0), 1);
  for (const [cod, w] of tr) {
    const ff = w > 0 ? (g - cum) / w : (g > cum ? 1 : 0);
    out[cod] = Math.round(Math.min(Math.max(ff, 0), 1) * 1e4) / 1e4;
    cum += w;
  }
  return out;
}

/** Cheia pachetului: id-ul stabil al motorului, sau compus la fel pentru programele mai vechi. */
export function cheia(e: EtapaProgram): string {
  return e.id ?? `${e.corp}/${e.nivel == null ? "-" : e.nivel}/${e.etapa}`;
}

export type StareSaptamana = {
  luni: Date;
  /** fiecare pachet, cu fracția și faza lui */
  pachete: Map<string, { e: EtapaProgram; s: Fractie }>;
  /** pachetele în lucru săptămâna asta */
  active: EtapaProgram[];
  /** oamenii pe șantier (histograma săptămânii) */
  oameni: number;
  /** macaralele cu fereastra deschisă: pe corp sau subsol → bucăți */
  macarale: Record<string, number>;
  /** șantierul (gard, barăci) e înființat: de la organizare până la exterioare */
  santier: boolean;
  /** nivelurile de structură ale fiecărui corp: câte turnate, și cel în lucru cu fracția */
  structura: Record<string, { gata: number; inLucru: { nivel: number; f: number; faza: FazaPachet["faza"] | null } | null }>;
  /** costul direct cumulat (fluxul lunar, lunile încheiate) */
  cost_eur: number;
};

export function stareaSaptamanii(p: Program, l: Date): StareSaptamana {
  const k = ISO(l);
  const pachete = new Map<string, { e: EtapaProgram; s: Fractie }>();
  const active: EtapaProgram[] = [];
  const structura: StareSaptamana["structura"] = {};
  for (const e of pachetele(p)) {
    const s = stare(e, l);
    pachete.set(cheia(e), { e, s });
    const f0 = e.desfasurare?.find((w) => w.luni === k);
    if (f0 || (!e.desfasurare && s.inceput && !s.gata)) active.push(e);
  }
  for (const c of p.corpuri) {
    let gata = 0; let inLucru: StareSaptamana["structura"][string]["inLucru"] = null;
    for (const n of [...c.niveluri].sort((a, b) => a.nivel - b.nivel)) {
      if (n.subsol) continue;
      const e = n.etape.find((x) => x.etapa === "E30");
      if (!e) continue;
      const s = pachete.get(cheia(e))!.s;
      if (s.f >= 1) gata += 1;
      else if (s.f > 0 && !inLucru) inLucru = { nivel: n.nivel, f: s.f, faza: s.faza };
    }
    structura[c.cod] = { gata, inLucru };
  }
  const h = p.histograma.find((w) => w.start === k);
  const macarale: Record<string, number> = {};
  const fereastra = (m: { start: string; sfarsit: string; buc: number } | null | undefined, cod: string) => {
    if (m && m.start <= k && k < m.sfarsit) macarale[cod] = m.buc;
  };
  for (const [cod, m] of Object.entries(p.macarale.pe_corp ?? {})) fereastra(m, cod);
  for (const [cod, m] of Object.entries(p.macarale.pe_subsol ?? {})) fereastra(m, cod);
  const e00 = p.santier.find((e) => e.etapa === "E00"), e99 = p.santier.find((e) => e.etapa === "E99");
  const santier = !!e00 && stare(e00, l).inceput && !(e99 && stare(e99, l).gata);
  let cost = 0;
  for (const m of p.flux?.luni ?? []) if (`${m.luna}-01` <= k) cost += m.cost_direct_eur;
  return { luni: l, pachete, active, oameni: Math.round(h?.total ?? 0), macarale, santier, structura, cost_eur: cost };
}

/** Jaloanele programului: ce se vede pe axa timpului ca reper. */
export function jaloanele(p: Program): { luni: Date; cheie: string; unitate?: string }[] {
  const out: { luni: Date; cheie: string; unitate?: string }[] = [];
  const la = (d: string) => lunea(new Date(d + "T00:00:00"));
  const e00 = p.santier.find((e) => e.etapa === "E00");
  if (e00) out.push({ luni: la(e00.start), cheie: "tl.jalon.organizare" });
  for (const b of p.subsoluri ?? []) {
    const sus = [...b.niveluri].sort((x, y) => y.nivel - x.nivel)[0]?.etape.find((e) => e.etapa === "E20");
    if (sus) out.push({ luni: la(sus.sfarsit), cheie: "tl.jalon.placa_sus", unitate: b.cod });
  }
  for (const c of p.corpuri) {
    const e30 = [...c.niveluri].sort((x, y) => x.nivel - y.nivel).find((n) => !n.subsol)?.etape.find((e) => e.etapa === "E30");
    if (e30) out.push({ luni: la(e30.start), cheie: "tl.jalon.structura", unitate: c.cod });
  }
  const e99 = p.santier.find((e) => e.etapa === "E99"), e100 = p.santier.find((e) => e.etapa === "E100");
  if (e99) out.push({ luni: la(e99.start), cheie: "tl.jalon.exterioare" });
  if (e100) out.push({ luni: la(e100.sfarsit), cheie: "tl.jalon.receptie" });
  return out.sort((a, b) => a.luni.getTime() - b.luni.getTime());
}
